/**
 * Saha formu — oluşturma ve düzenleme AYNI bileşen.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * NEDEN TEK BİLEŞEN
 *
 * İki ekran yazmak ilk gün daha hızlıdır; ikinci ayda alanların biri yalnız
 * birine eklenir ve kullanıcı "ekleyebiliyorum ama düzenleyemiyorum" der.
 * Fark yalnız iki yerde: başlangıç değerleri ve gönderilen gövde
 * (POST tamamı, PATCH yalnız değişenler).
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useParams } from '@tanstack/react-router';
import { siteCreateSchema, type SiteCreateBody, type SiteCreateInput } from '@gasco/shared';

import { Field, Input, Select } from '../design/Field.tsx';
import {
  FormKabugu,
  IzinKapisi,
  KayitKapisi,
  KaydetmeHatasi,
  SecimYerTutucu,
  degisenAlanlar,
  sayiAlani,
  useKaydet,
} from './form-parts.tsx';
import { useMusteriler, useSaha, type SahaDetayi } from './api.ts';

/** Koordinatlar TEK PARÇADIR — yarım koordinat yeni değil, YANLIŞ konumdur. */
const BIRLIKTE = [['latitude', 'longitude']] as const;

export function SahaYeni(): React.JSX.Element {
  return (
    <IzinKapisi gerekli="site_create">
      <SahaFormu />
    </IzinKapisi>
  );
}

export function SahaDuzenle(): React.JSX.Element {
  const { sahaId } = useParams({ from: '/korumali/sahalar/$sahaId/duzenle' });
  const sorgu = useSaha(Number(sahaId));

  return (
    <IzinKapisi gerekli="site_edit">
      <KayitKapisi sorgu={sorgu} bulunamadiBaslik="Saha bulunamadı" donus="/sahalar">
        {(detay) => <SahaFormu mevcut={detay} />}
      </KayitKapisi>
    </IzinKapisi>
  );
}

function baslangic(mevcut?: SahaDetayi): SiteCreateInput {
  const s = mevcut?.site;
  return {
    addrCode: s?.addr_code ?? '',
    siteCode: s?.site_code ?? '',
    name: s?.name ?? '',
    city: s?.city ?? '',
    district: s?.district ?? '',
    address: s?.address ?? '',
    // Yeni sahada koordinat BOŞ bırakılır; uydurma bir varsayılan (Ankara)
    // koymak, girilmiş gibi görünen yanlış bir konum üretirdi.
    latitude: s?.latitude ?? (undefined as unknown as number),
    longitude: s?.longitude ?? (undefined as unknown as number),
    contactPerson: s?.contact_person ?? '',
    contactPhone: s?.contact_phone ?? '',
    contactEmail: s?.contact_email ?? '',
    customerId: s?.customer_id ?? null,
  };
}

function SahaFormu({ mevcut }: { readonly mevcut?: SahaDetayi }): React.JSX.Element {
  const duzenleme = mevcut !== undefined;
  const donus = duzenleme ? `/sahalar/${String(mevcut.site.id)}` : '/sahalar';
  const { kaydet, hata } = useKaydet(donus);
  const musteriler = useMusteriler();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<SiteCreateInput, unknown, SiteCreateBody>({
    resolver: zodResolver(siteCreateSchema),
    defaultValues: baslangic(mevcut),
  });

  const gonder = handleSubmit(async (degerler) => {
    if (duzenleme) {
      const govde = degisenAlanlar(degerler, dirtyFields, BIRLIKTE);
      // Hiçbir şey değişmediyse sunucuya gitmiyoruz: `no_changes` hatası
      // teknik olarak doğru ama kullanıcı bir yanlış yapmadı.
      if (Object.keys(govde).length === 0) return;
      await kaydet(`/sites/${String(mevcut.site.id)}`, 'PATCH', govde);
      return;
    }
    await kaydet('/sites', 'POST', degerler);
  });

  return (
    <FormKabugu
      baslik={duzenleme ? 'Sahayı düzenle' : 'Yeni saha'}
      aciklama={duzenleme ? mevcut.site.name : 'Saha yapılandırmanın birincil kaydıdır'}
      donus={donus}
      gonderiliyor={isSubmitting}
      onSubmit={(e) => void gonder(e)}
    >
      <Field
        label="Tesisat/abone kodu"
        required
        hint="Firma içinde benzersiz olmalı"
        error={errors.addrCode?.message}
      >
        {({ id, required, describedBy, invalid }) => (
          <Input
            id={id}
            required={required}
            aria-describedby={describedBy}
            invalid={invalid}
            {...register('addrCode')}
          />
        )}
      </Field>

      <Field label="Saha adı" required error={errors.name?.message}>
        {({ id, required, describedBy, invalid }) => (
          <Input
            id={id}
            required={required}
            aria-describedby={describedBy}
            invalid={invalid}
            {...register('name')}
          />
        )}
      </Field>

      <Field
        label="Saha kodu"
        hint="Dış sistem kodu — zorunlu değil"
        error={errors.siteCode?.message}
      >
        {({ id, describedBy, invalid }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            invalid={invalid}
            {...register('siteCode')}
          />
        )}
      </Field>

      {/*
       * Müşteri İSTEĞE BAĞLI [K-001]: müşterisiz saha (depo, stok sahası)
       * meşrudur. Listenin ilk seçeneği bu yüzden "müşteri yok".
       */}
      <Field
        label="Müşteri"
        hint="Müşterisiz saha da kaydedilebilir"
        error={errors.customerId?.message}
      >
        {({ id, describedBy, invalid }) =>
          musteriler.data === undefined ? (
            <SecimYerTutucu id={id} describedBy={describedBy} />
          ) : (
            <Select
              id={id}
              aria-describedby={describedBy}
              invalid={invalid}
              {...register('customerId', {
                setValueAs: (v: unknown) => (v === '' || v === null ? null : Number(v)),
              })}
            >
              <option value="">Müşteri yok</option>
              {musteriler.data.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.title}
                </option>
              ))}
            </Select>
          )
        }
      </Field>

      <Field label="Şehir" error={errors.city?.message}>
        {({ id, describedBy, invalid }) => (
          <Input id={id} aria-describedby={describedBy} invalid={invalid} {...register('city')} />
        )}
      </Field>

      <Field label="İlçe" error={errors.district?.message}>
        {({ id, describedBy, invalid }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            invalid={invalid}
            {...register('district')}
          />
        )}
      </Field>

      <Field label="Adres" error={errors.address?.message}>
        {({ id, describedBy, invalid }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            invalid={invalid}
            {...register('address')}
          />
        )}
      </Field>

      {/*
       * Koordinat İKİSİ BİRDEN zorunlu. Sunucu yarım koordinatı
       * `incomplete_coordinates` ile reddediyor; burada ikisi de zorunlu
       * alan olduğu için o hataya hiç düşülmüyor — ve düzenlemede biri
       * değişirse ikisi birden gönderiliyor (`BIRLIKTE`).
       */}
      <Field
        label="Enlem"
        required
        hint="Örn. 38.501234 — harita ekranı (Görev 6) bunu kullanacak"
        error={errors.latitude?.message}
      >
        {({ id, required, describedBy, invalid }) => (
          <Input
            id={id}
            required={required}
            aria-describedby={describedBy}
            invalid={invalid}
            type="number"
            step="any"
            inputMode="decimal"
            {...register('latitude', sayiAlani)}
          />
        )}
      </Field>

      <Field label="Boylam" required error={errors.longitude?.message}>
        {({ id, required, describedBy, invalid }) => (
          <Input
            id={id}
            required={required}
            aria-describedby={describedBy}
            invalid={invalid}
            type="number"
            step="any"
            inputMode="decimal"
            {...register('longitude', sayiAlani)}
          />
        )}
      </Field>

      <Field label="İlgili kişi" error={errors.contactPerson?.message}>
        {({ id, describedBy, invalid }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            invalid={invalid}
            {...register('contactPerson')}
          />
        )}
      </Field>

      <Field label="Telefon" error={errors.contactPhone?.message}>
        {({ id, describedBy, invalid }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            invalid={invalid}
            type="tel"
            inputMode="tel"
            {...register('contactPhone')}
          />
        )}
      </Field>

      <Field label="E-posta" error={errors.contactEmail?.message}>
        {({ id, describedBy, invalid }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            invalid={invalid}
            type="email"
            inputMode="email"
            autoCapitalize="none"
            {...register('contactEmail')}
          />
        )}
      </Field>

      <KaydetmeHatasi hata={hata} />
    </FormKabugu>
  );
}
